import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, LinearProgress, Chip } from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  TrendingUp,
  TrendingDown,
  Business,
  Person,
  Star,
  EmojiEvents
} from '@mui/icons-material';

interface ScoreCardDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
  'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
  'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
  'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
  'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
  'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
};

const ScoreCardDashboard: React.FC<ScoreCardDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      all: scores,
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
      topScore: Math.max(...scores.map(s => s.value)),
      lowestScore: Math.min(...scores.map(s => s.value))
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const getStatusIcon = (score: number) => {
    if (score >= 80) return <CheckCircle sx={{ color: '#27ae60', fontSize: 28 }} />;
    if (score >= 60) return <CheckCircle sx={{ color: '#2ecc71', fontSize: 28 }} />;
    if (score >= 40) return <Warning sx={{ color: '#f39c12', fontSize: 28 }} />;
    return <Error sx={{ color: '#e74c3c', fontSize: 28 }} />;
  };

  const getGrade = (score: number) => {
    if (score >= 90) return 'A+';
    if (score >= 80) return 'A';
    if (score >= 70) return 'B+';
    if (score >= 60) return 'B';
    if (score >= 50) return 'C+';
    if (score >= 40) return 'C';
    return 'D';
  };

  const getStatusLabel = (score: number) => {
    if (language === 'he') {
      if (score >= 80) return 'ביצועים מצוינים';
      if (score >= 60) return 'ביצועים טובים';
      if (score >= 40) return 'ביצועים סבירים';
      return 'דורש התייחסות';
    } else {
      if (score >= 80) return 'Excellent Performance';
      if (score >= 60) return 'Good Performance';
      if (score >= 40) return 'Fair Performance';
      return 'Needs Attention';
    }
  };

  const renderScoreCard = (category: any, rank?: number) => (
    <Grid item xs={12} sm={6} md={4} key={category.key}>
      <Card
        elevation={4}
        sx={{
          height: '100%',
          background: `linear-gradient(135deg, ${getScoreColor(category.value)}15, ${getScoreColor(category.value)}05)`,
          border: `2px solid ${getScoreColor(category.value)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 8
          }
        }}
      >
        <CardContent>
          {/* Header with icon and status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h2" sx={{ fontSize: '42px' }}>
              {category.icon}
            </Typography>
            {getStatusIcon(category.value)}
          </Box>

          {/* Category name */}
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, minHeight: '48px' }}>
            {category.name}
          </Typography>

          {/* Score display */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{ color: getScoreColor(category.value) }}
            >
              {category.value.toFixed(0)}
            </Typography>
            <Typography variant="h5" sx={{ color: '#666' }}>%</Typography>
            <Box sx={{ ml: 'auto' }}>
              <Chip
                label={getGrade(category.value)}
                sx={{
                  backgroundColor: getScoreColor(category.value),
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  height: '32px'
                }}
              />
            </Box>
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={category.value}
            sx={{
              height: 8,
              borderRadius: 4,
              mb: 2,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getScoreColor(category.value),
                borderRadius: 4
              }
            }}
          />

          {/* Status label */}
          <Typography variant="body2" fontWeight="600" sx={{ color: getScoreColor(category.value), mb: 1 }}>
            {getStatusLabel(category.value)}
          </Typography>

          {/* Additional info */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {language === 'he' ? 'מדד ביצועים' : 'Performance Index'}
            </Typography>
            {rank && rank <= 3 && (
              <Chip
                icon={<Star />}
                label={`#${rank}`}
                size="small"
                sx={{ backgroundColor: '#ffd700', color: '#333', fontWeight: 'bold' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  // Get top performers
  const topPerformers = [...processedScores.all]
    .sort((a, b) => b.value - a.value)
    .slice(0, 3);

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '🏆 כרטיסי ציון מפורטים' : '🏆 Detailed Score Cards'}
      </Typography>

      {/* Hero Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent>
              <Business sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {language === 'he' ? 'ביצועים עסקיים' : 'Business Performance'}
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {processedScores.businessAverage.toFixed(1)}%
              </Typography>
              <Chip
                label={getGrade(processedScores.businessAverage)}
                sx={{ mt: 1, backgroundColor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 'bold' }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent>
              <Person sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {language === 'he' ? 'ביצועים אישיים' : 'Personal Performance'}
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {processedScores.personalAverage.toFixed(1)}%
              </Typography>
              <Chip
                label={getGrade(processedScores.personalAverage)}
                sx={{ mt: 1, backgroundColor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 'bold' }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent>
              <TrendingUp sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {language === 'he' ? 'ציון הגבוה ביותר' : 'Highest Score'}
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {processedScores.topScore.toFixed(0)}%
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, mt: 1, display: 'block' }}>
                {topPerformers[0].name}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              height: '100%'
            }}
          >
            <CardContent>
              <EmojiEvents sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                {language === 'he' ? 'מספר הישגים' : 'Achievements'}
              </Typography>
              <Typography variant="h3" fontWeight="bold">
                {processedScores.all.filter(s => s.value >= 60).length}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, mt: 1, display: 'block' }}>
                {language === 'he' ? 'קטגוריות מעל 60%' : 'Categories above 60%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business Categories */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '3px solid #667eea' }}>
          🏢 {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        <Grid container spacing={3}>
          {processedScores.business.map(cat => {
            const rank = topPerformers.findIndex(t => t.key === cat.key) + 1;
            return renderScoreCard(cat, rank || undefined);
          })}
        </Grid>
      </Paper>

      {/* Personal Categories */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, pb: 1, borderBottom: '3px solid #f5576c' }}>
          👤 {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        <Grid container spacing={3}>
          {processedScores.personal.map(cat => {
            const rank = topPerformers.findIndex(t => t.key === cat.key) + 1;
            return renderScoreCard(cat, rank || undefined);
          })}
        </Grid>
      </Paper>

      {/* Top Performers */}
      <Paper elevation={3} sx={{ p: 3, mt: 3, background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <EmojiEvents sx={{ fontSize: 32, color: '#d35400' }} />
          <Typography variant="h6" fontWeight="bold">
            {language === 'he' ? 'מובילים' : 'Top Performers'}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {topPerformers.map((cat, idx) => (
            <Grid item xs={12} sm={4} key={cat.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#d35400' }}>
                  {idx + 1}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="600">
                    {cat.icon} {cat.name}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: getScoreColor(cat.value) }}>
                    {cat.value.toFixed(0)}%
                  </Typography>
                </Box>
                {idx === 0 && <Star sx={{ color: '#ffd700', fontSize: 32 }} />}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ScoreCardDashboard;
